import { Globe, tile, utils, zeroTexture } from "@hscmap/stellar-globe"
import axios from 'axios'
import { TileTextureProviderComponentBase } from '@/stellar-globe-components'
import { MixerType } from "@/modules/SspDataModule"
import Component from "vue-class-component"
import { Prop } from "vue-property-decorator"
import { Params } from "../"
import { ImageFilter } from '@/utils/image_filter'
import { store } from '@/store'
import { range } from '@/utils/range'

@Component
export default class TextureProviderComponent extends TileTextureProviderComponentBase<TextureProvider> {
  @Prop({ required: true })
  baseUrl!: string

  @Prop({ required: true })
  params!: Params

  created() {
    this.$watch(() => [this.params.filter, this.params.type, this.params.simpleColorMatrix.colors.map(c => c.enabled)], () => {
      this.updateParams(true)
      this.tp.setImageFilter()
    })
    this.$watch(() => this.params, () => {
      this.updateParams()
    }, { deep: true })
    this.$watch(() => store.config.tileLayer.enableMagFilter, () => {
      this.updateParams(true)
    })
  }

  newTextureProvider() {
    return new TextureProvider(this.globe, this.baseUrl, this)
  }
}

class SimpleRgbMixerPng extends ImageFilter {
  constructor(gl: WebGLRenderingContext, readonly params: Params["simpleRgb"]) {
    super(gl)
  }

  fragmentShader() {
    return require('./glsl/simple_rgb_png_mixer.frag.glsl') as string
  }

  setUniformParams() {
    this.program.uniform1f({
      u_a: this.params.a,
      u_bias: this.params.bias,
      u_beta: this.params.beta,
      u_b0: this.params.b0,
    })
  }
}

class SdssTrueColorMixerPng extends ImageFilter {
  constructor(gl: WebGLRenderingContext, readonly params: Params["sdssTrueColor"]) {
    super(gl)
  }

  fragmentShader() {
    return require('./glsl/sdss_true_color_png_mixer.frag.glsl') as string
  }

  setUniformParams() {
    this.program.uniform1f({
      u_a: this.params.a,
      u_bias: this.params.bias,
      u_beta: this.params.beta,
      u_b0: this.params.b0,
    })
  }
}

class SimpleColorMatrixPng extends ImageFilter {
  constructor(gl: WebGLRenderingContext, readonly params: Params["simpleColorMatrix"]) {
    super(gl)
  }

  fragmentShader() {
    const sinh = require('./glsl/sinh.glsl') as string
    const decode_png = require('./glsl/decode_png.glsl') as string
    const simple_rgb = require('./glsl/simple_rgb.glsl') as string
    // 出力は
    // r = \sum_i colors[i][0]
    // g = \sum_i colors[i][1]
    // b = \sum_i colors[i][2]
    // raw = decode_png(vec3(r, g, b))
    // float t0 = texture2D(u_texture0, v_coord).r,
    //       t1 = texture2D(u_texture1, v_coord).r, ...;
    // float r = color[0][0] * t0 + color[1][0] * t1 + ...;
    // float g = color[0][1] * t0 + color[1][1] * t1 + ...;

    const filters = this.params.colors.filter(c => c.enabled)
    const n = filters.length
    const ts = range(n).map(i => `t${i} = texture2D(u_texture${i}, v_coord).r`).join(', ')
    const r = range(n).map(i => `u_color[${i}][0] * t${i}`).join(' + ')
    const g = range(n).map(i => `u_color[${i}][1] * t${i}`).join(' + ')
    const b = range(n).map(i => `u_color[${i}][2] * t${i}`).join(' + ')

    const fragShader = `
      precision highp float;
      uniform sampler2D ${range(n).map(t => `u_texture${t}`)};
      uniform vec3  u_color[${n}];
      uniform float u_beta;
      uniform float u_a;
      uniform float u_bias;
      uniform float u_b0;
      varying vec2 v_coord;

      ${sinh}
      ${decode_png}
      ${simple_rgb}
      
      void main(void){
        float ${ts};
        vec3 raw = decode_png(vec3(${r}, ${g}, ${b}));
        vec3 color = simple_rgb(raw, u_beta, u_a, u_bias, u_b0);
        gl_FragColor = vec4(color, 1.);
      }
    `
    return fragShader
  }

  setUniformParams() {
    this.program.uniform3fv(Object.fromEntries(
      this.params.colors.filter(c => c.enabled).map((c, i) => [`u_color[${i}]`, Array.from(c.value)])
    ))
    this.program.uniform1f({
      u_a: this.params.a,
      u_bias: this.params.bias,
      u_beta: this.params.beta,
      u_b0: this.params.b0,
    })
  }
}


export class TextureProvider extends tile.AsyncTextureProvider {
  private tracts: tile.Tract[] = []
  private tractName = new Map<tile.Tract, string>()
  private imageFilter!: ImageFilter

  constructor(globe: Globe, public baseUrl: string, readonly ttc: TextureProviderComponent) {
    super(globe)
    this.setImageFilter()
    this.setBaseUrl(baseUrl)
  }

  release() {
    this.imageFilter.release()
    super.release()
  }

  private setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl
    this.loadMetadata()
  }

  setImageFilter() {
    this.imageFilter && this.imageFilter.release()
    switch (this.ttc.params.type) {
      case MixerType.SDSS_TRUE_COLOR:
        this.imageFilter = new SdssTrueColorMixerPng(this.globe.gl, this.ttc.params.sdssTrueColor)
        break
      case MixerType.SIMPLE_RGB:
        this.imageFilter = new SimpleRgbMixerPng(this.globe.gl, this.ttc.params.simpleRgb)
        break
      case MixerType.SIMPLE_COLOR_MATRIX:
        this.imageFilter = new SimpleColorMatrixPng(this.globe.gl, this.ttc.params.simpleColorMatrix)
        break
    }
  }

  private async loadMetadata() {
    const { baseUrl } = this
    const { data: tracts } = await axios.get(`${this.baseUrl}/meta.json`)
    if (baseUrl !== this.baseUrl) {
      return
    }
    this.tracts = []
    this.tractName.clear()
    for (const name of Object.keys(tracts)) {
      const tract = tile.Tract.fromFitsHeader((tracts as any)[name].wcs)
      this.tractName.set(tract, name)
      this.tracts.push(tract)
    }
  }

  walkTracts(cb: (tract: tile.Tract) => void) {
    for (const t of this.tracts) {
      cb(t)
    }
  }

  private imageUrls(ref: tile.TileRef) {
    if (this.ttc.params.type == MixerType.SIMPLE_COLOR_MATRIX) {
      return this.ttc.params.simpleColorMatrix.colors.filter(c => c.enabled)
        .map(c => c.filterName)
        .map(f => `${this.baseUrl}/${f}/${this.tractName.get(ref.tract)}/${ref.level}/${ref.p}/${ref.q}.png`)
    }
    else {
      return this.ttc.params.filter
        .map((f) => `${this.baseUrl}/${f}/${this.tractName.get(ref.tract)}/${ref.level}/${ref.p}/${ref.q}.png`)
    }
  }

  async makeTileTexture(ref: tile.TileRef) {
    const tt = new tile.TileTexture(this)
    const urls = this.imageUrls(ref)
    const images = await Promise.all(urls.map((url) => utils.loadImage(url, zeroTexture)))
    const gl = this.globe.gl
    const { tileSize } = ref.tract

    if (this.alreadyReleased) {
      return tt
    }

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1)
    this.imageFilter.applyToImages(tt.tex, tileSize, tileSize, images)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0)
    tt.tex.bind(() => {
      if (ref.level === ref.tract.maxTileLevel) {
        gl.generateMipmap(gl.TEXTURE_2D)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR)
      }
      if (!this.ttc.Store.config.tileLayer.enableMagFilter && ref.level === 0) {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
      }
    })
    return tt
  }
}
